import { useEffect, useState } from "react";
import { Stethoscope, Save, ShieldCheck, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { Button } from "../../components/ui/button";
import { cn } from "../../utils/tailwindUtils";
import { INTEGRATION_CONNECTION } from "../../constants/api";
import { SESSION_STORAGE_HYDRATED_EVENT } from "../../hooks/use-navigation";

const EMPTY_FORM = {
    base_url: "",
    service_key: "",
    external_org_id: "",
    external_branch_id: "",
    external_clinic_id: "",
    is_active: false,
};

const inputCls =
    "w-full px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-sidebar-primary";

// Persist whether a connection exists so the left-sidebar "Integration" item
// (see use-navigation.js) can show/hide. Dispatching the hydrate event makes the
// sidebar re-render immediately, without a page refresh.
const setConnectedFlag = (connected) => {
    sessionStorage.setItem("emedhubConnected", JSON.stringify(!!connected));
    window.dispatchEvent(new Event(SESSION_STORAGE_HYDRATED_EVENT));
};

/**
 * Settings > Integrations — the eMedHub *connection* form only.
 *
 * The operational side (running a sync, reviewing/linking doctors) lives on the
 * dedicated "Integration" screen in the left sidebar, which only appears once a
 * connection has been saved here.
 */
const ConnectEmedHub = () => {
    const navigate = useNavigate();
    const { fetch, update } = useApiMethods();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [hasKey, setHasKey] = useState(false);
    const [hasConnection, setHasConnection] = useState(false);

    const loadConnection = async () => {
        const res = await fetch(INTEGRATION_CONNECTION, {}, setLoading);
        const connected = !!(res?.status === "success" && res.data);
        setHasConnection(connected);
        setConnectedFlag(connected);
        if (connected) {
            const d = res.data;
            setForm({
                base_url: d.base_url || "",
                service_key: "", // never prefill the secret
                external_org_id: d.external_org_id ?? "",
                external_branch_id: d.external_branch_id ?? "",
                external_clinic_id: d.external_clinic_id ?? "",
                is_active: !!d.is_active,
            });
            setHasKey(!!d.service_key);
        }
    };

    useEffect(() => {
        loadConnection();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onChange = (key, value) => setForm((f) => ({ ...f, [key]: value }));

    const handleSave = async () => {
        if (!form.base_url) {
            ToastMsg("error", "Provider base URL is required");
            return;
        }
        const payload = { ...form };
        // Don't send an empty secret — keeps the stored one intact.
        if (!payload.service_key) delete payload.service_key;
        const res = await update(INTEGRATION_CONNECTION, payload, false, setSaving);
        if (res?.status === "success") {
            ToastMsg("success", "Connection saved");
            setConnectedFlag(true);
            loadConnection();
        } else {
            ToastMsg("error", res?.message || "Failed to save connection");
        }
    };

    const isAdmin =
        JSON.parse(sessionStorage.getItem("is_admin") || "false") ||
        JSON.parse(sessionStorage.getItem("is_super_admin") || "false");
    if (!isAdmin) {
        return (
            <div className="flex flex-col items-center justify-center py-20 text-center gap-3">
                <ShieldCheck className="w-12 h-12 text-amber-500" />
                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                    You don't have permission to manage the eMedHub integration.
                </p>
            </div>
        );
    }

    return (
        <div className="max-w-3xl space-y-6 mt-8 border-t border-gray-100 dark:border-gray-700 pt-8">
            {/* Header */}
            <div className="flex items-start gap-3">
                <div className="w-11 h-11 flex-shrink-0 rounded-xl bg-sidebar/10 text-sidebar-primary grid place-items-center">
                    <Stethoscope size={22} />
                </div>
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                            eMedHub (HIMS) Integration
                        </h2>
                        <span
                            className={cn(
                                "inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border",
                                form.is_active
                                    ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800"
                                    : "bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700"
                            )}
                        >
                            <span className={cn("w-1.5 h-1.5 rounded-full", form.is_active ? "bg-green-500" : "bg-gray-400")} />
                            {form.is_active ? "Enabled" : "Disabled"}
                        </span>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Link this company to an eMedHub clinic. Once saved, use the{" "}
                        <strong className="font-medium">Integration</strong> screen in the left
                        menu to sync doctors and review the links.
                    </p>
                </div>
            </div>

            {loading ? (
                <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
                    <div className="animate-pulse space-y-3">
                        <div className="h-10 w-2/3 bg-gray-100 dark:bg-gray-700 rounded" />
                        <div className="h-16 w-full bg-gray-100 dark:bg-gray-700 rounded" />
                    </div>
                </div>
            ) : (
                <>
                    {/* Connection form */}
                    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm p-5 space-y-4">
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">Connection</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="sm:col-span-2">
                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                    Provider base URL
                                </label>
                                <input
                                    className={inputCls}
                                    placeholder="https://api.emedhub.in"
                                    value={form.base_url}
                                    onChange={(e) => onChange("base_url", e.target.value)}
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                    Service key {hasKey && <span className="text-green-600">(saved — leave blank to keep)</span>}
                                </label>
                                <input
                                    type="password"
                                    autoComplete="new-password"
                                    className={inputCls}
                                    placeholder={hasKey ? "••••••••" : "Paste the X-Service-Key from eMedHub"}
                                    value={form.service_key}
                                    onChange={(e) => onChange("service_key", e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                    eMedHub Org ID
                                </label>
                                <input
                                    type="number"
                                    className={inputCls}
                                    value={form.external_org_id}
                                    onChange={(e) => onChange("external_org_id", e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                    eMedHub Branch ID
                                </label>
                                <input
                                    type="number"
                                    className={inputCls}
                                    value={form.external_branch_id}
                                    onChange={(e) => onChange("external_branch_id", e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                    eMedHub Clinic ID
                                </label>
                                <input
                                    type="number"
                                    className={inputCls}
                                    value={form.external_clinic_id}
                                    onChange={(e) => onChange("external_clinic_id", e.target.value)}
                                />
                            </div>
                            <div className="flex items-center gap-2 pt-6">
                                <input
                                    id="emedhub-active"
                                    type="checkbox"
                                    className="w-4 h-4 accent-sidebar-primary"
                                    checked={form.is_active}
                                    onChange={(e) => onChange("is_active", e.target.checked)}
                                />
                                <label htmlFor="emedhub-active" className="text-sm text-gray-700 dark:text-gray-300">
                                    Enable for payroll
                                </label>
                            </div>
                        </div>
                        <div className="pt-1 flex items-center gap-3 flex-wrap">
                            <Button onClick={handleSave} disabled={saving} variant="brand">
                                <Save size={14} className="mr-2" />
                                {saving ? "Saving..." : "Save Connection"}
                            </Button>
                            {hasConnection && (
                                <Button variant="outline" onClick={() => navigate("/integration")}>
                                    Go to Integration <ArrowRight size={14} className="ml-2" />
                                </Button>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ConnectEmedHub;
